/**
 * The map itself — seven axes, fourteen directions, and the node progressions
 * that have actually been written.
 *
 * ## The 41-point direction (2026-08-17)
 *
 * `DESIGN.md` §8's 20-point spoke — eleven nodes in a straight line, every
 * one bought on the way to a nineteenth-point Epic — is **superseded**. A
 * direction is now a branching body of ordinary nodes, ~60–80 points deep
 * when fully written, of which a god needs **40 ordinary points and then a
 * 41st for the Epic**. Three consequences, each a constant below:
 *
 *  · `EPIC_REQUIRES` is 40, so `EPIC_JOURNEY` — the least a transcendence
 *    can cost — is 41.
 *  · A god may hold **at most two Epics** (`MAX_EPICS`), and the arithmetic
 *    agrees with the rule rather than merely accompanying it: the whole
 *    theoretical budget is 100 level points + 20 Legacy = 120, and three
 *    journeys are 123. An ancient god is *not* complete. That is the shape.
 *  · Layers open on **depth of investment** (`TIER_FLOOR`) rather than on
 *    having bought every node beneath — so a layer holds more choices than
 *    the next one asks for, and the surplus is the branch.
 *
 * ## What is written, and to which shape
 *
 *  · **Reach** and **Root** carry D §8.3–8.4's spokes unchanged: 18 ordinary
 *    points each, written to the old shape. They are kept as content, and
 *    under the new rule they are honestly **unfinished** — 18 of the 40 an
 *    Epic asks for — so their Epics are unreachable until they are rewritten,
 *    and `canChooseEpic` says so in as many words. Their outer nodes stay
 *    reachable: a floor is never set above what a thin direction actually
 *    holds beneath it (`floorOf`).
 *  · **Beasts** is the first direction written to the new shape (`BEASTS`
 *    below): three Currents — herd · blood · voice — running through five
 *    layers, more nodes than the Epic needs, and its Epics gated on lived
 *    history. Its inner layers are wired (`bindings.ts` says which);
 *    its outer Blood and Voice nodes are written and honestly `absent`.
 *  · The other **eleven** have an identity (D §7.1) and three Epics (D §9)
 *    and nothing in between, which D Appendix B.2 names as the next design
 *    pass. Nothing is invented here to fill that in. An unworked direction
 *    carries one Awakening rung and its Epics, and the UI draws the rest of
 *    its spoke empty — so the hole is visible in the tool that would
 *    otherwise hide it.
 *
 * Pure by law 7.
 */

import type {
  Axis, Crossroad, Current, Direction, DirectionId, Epic, NodeTier, WebNode,
} from './types';
import { EPICS } from './epics';

/* ── the budget ─────────────────────────────────────────────────────────── */

/**
 * Ordinary points a direction must hold before its Epic opens. Forty is the
 * whole shape of the 2026-08-17 ruling: deep enough that a transcendence is
 * a life's work in that direction, and priced so that two are possible and
 * three never are (see `MAX_EPICS`).
 */
export const EPIC_REQUIRES = 40;

/** The least a transcendence can cost: forty ordinary points and the Epic's own. */
export const EPIC_JOURNEY = EPIC_REQUIRES + 1;

/**
 * A god may ultimately possess at most two Epics. This is a rule and not
 * only an arithmetic accident — `canChooseEpic` refuses a third outright —
 * but the arithmetic agrees: `POINTS_AT_LEVEL_100 + LEGACY_POINTS_CEILING`
 * is 120 and three journeys are 123, so even a god at the top of every track
 * cannot buy a third by any route. `test/godhood.ts` asserts both halves.
 */
export const MAX_EPICS = 2;

/**
 * How many ordinary points a FULLY WRITTEN direction is meant to hold —
 * the design target for the branch, not a cap the engine enforces. What a
 * direction actually holds is `ordinaryPointsIn`, read off its nodes; the
 * engine's only cap is the nodes themselves. Kept as one number so `breadth`
 * has a denominator that means "of the whole Web as designed".
 */
export const DIRECTION_CAPACITY = 80;

/** D §6.2: "Levels 1–100 grant approximately 100 normal Godhood Points." */
export const POINTS_AT_LEVEL_100 = 100;

/**
 * D §6.2's "working ceiling ~20 additional special points". Kept separate
 * from the level grant so nothing can quietly turn Legacy into a second
 * level track.
 */
export const LEGACY_POINTS_CEILING = 20;

/**
 * Depth a layer asks for: ordinary points a god must already have in the
 * direction — anywhere in it — before a node of that tier can be bought.
 *
 * Read against the felt progression the ruling wants (READ ~1–5, ACT ~6–12,
 * CONNECT ~13–20, SHAPE/PERSIST/BECOME ~21–40) and deliberately not
 * overfitted: the transcendence layer opens at 20 and its deepest nodes carry
 * their own `floor` (30, 36) rather than the table growing two more rows.
 *
 * `floorOf` caps every floor at what the direction actually holds beneath
 * that tier, so a direction written to the old thin shape (Reach, Root: 14
 * points below transcendence) keeps its outer nodes reachable — the old
 * every-node rule is exactly the degenerate case of this one.
 */
export const TIER_FLOOR: Readonly<Record<NodeTier, number>> = {
  awakening: 0,
  inclination: 1,
  practice: 5,
  mastery: 12,
  transcendence: 20,
  epic: EPIC_REQUIRES,
};

/* ── the seven tensions (D §7) ─────────────────────────────────────────── */

export const AXES: readonly Axis[] = [
  { id: 'living-world', name: 'Living World', poles: ['life', 'death'],
    question: 'How does the deity understand living systems and endings?' },
  { id: 'subjects', name: 'Subjects of Attention', poles: ['mortals', 'beasts'],
    question: 'Which kinds of minds and populations does the deity learn to read deeply?' },
  { id: 'structure', name: 'Structure', poles: ['order', 'chaos'],
    question: 'Does the deity reinforce stable patterns or increase novelty?' },
  { id: 'spatial', name: 'Spatial Godhood', poles: ['reach', 'root'],
    question: 'Does divinity expand by encompassing geography, or by embedding in one place?' },
  { id: 'expression', name: 'Expression', poles: ['touch', 'aura'],
    question: 'Does the deity act through deliberate local intervention or ambient influence?' },
  { id: 'causality', name: 'Causality', poles: ['might', 'insight'],
    question: 'Does the deity achieve change by force, or by leverage and understanding?' },
  { id: 'source', name: 'Source of Divinity', poles: ['sovereignty', 'communion'],
    question: 'Does power remain intrinsic, or become entangled with minds and belief?' },
];

const dir = (
  id: DirectionId, axis: Axis['id'], name: string, identity: string, worked = false,
  currents?: readonly Current[],
): Direction => ({ id, axis, name, identity, worked, ...(currents ? { currents } : {}) });

/** The three Beast Currents — visible themes, never subclasses. */
export const BEAST_CURRENTS: readonly Current[] = ['herd', 'blood', 'voice'];

/** D §7.1, verbatim in substance. */
export const DIRECTIONS: readonly Direction[] = [
  dir('life', 'living-world', 'Life',
    'Growth, recovery, lineage, reproduction, adaptation, ecological interdependence.'),
  dir('death', 'living-world', 'Death',
    'Decomposition, predation, culling, turnover, endings, release, nutrient return.'),
  dir('mortals', 'subjects', 'Mortals',
    'People, families, bands, language, culture, institutions, politics, religion, collective memory.'),
  dir('beasts', 'subjects', 'Beasts',
    'Individuals, packs, herds, breeding, migration, predation, territory, animal communication.',
    true, BEAST_CURRENTS),
  dir('order', 'structure', 'Order',
    'Cohesion, continuity, migration routes, institutions, tradition, ecological stability.'),
  dir('chaos', 'structure', 'Chaos',
    'Novelty, dissent, dispersal, disturbance, experimentation, branching, rapid adaptation.'),
  dir('reach', 'spatial', 'Reach',
    'Lift, altitude, traversal, distant perception, broad available Focus, geographic mobility.', true),
  dir('root', 'spatial', 'Root',
    'Resonance, anchors, sacred geography, local sensing, enduring place relationships.', true),
  dir('touch', 'expression', 'Touch',
    'Precision, Grasp, concentrated Focus, direct causal compression, repeated tending.'),
  dir('aura', 'expression', 'Aura',
    'Continuous low-intensity influence, persistent fields, environmental attunement, afterglow.'),
  dir('might', 'causality', 'Might',
    'Impulse capacity, raw work, Strike and Grasp force, persistence against opposition.'),
  dir('insight', 'causality', 'Insight',
    'Viability, leverage, causal relationships, pressure detection, downstream consequence.'),
  dir('sovereignty', 'source', 'Sovereignty',
    'Intrinsic Impulse, independence from Faith, unwitnessed power, identity resistance.'),
  dir('communion', 'source', 'Communion',
    'Prayer, belief, shared ritual, distributed perception, willing mortal action, cultural memory.'),
];

/* ── the two worked directions (D §8.3, §8.4) ──────────────────────────── */

type NodeSpec = Omit<WebNode, 'id' | 'direction'> & { slug: string };

const spoke = (direction: DirectionId, specs: readonly NodeSpec[]): WebNode[] =>
  specs.map(({ slug, ...rest }) => ({ ...rest, id: `${direction}.${slug}`, direction }));

/** D §8.3. */
const REACH: readonly NodeSpec[] = [
  { slug: 'unbound-presence', position: 1, tier: 'awakening', name: 'Unbound Presence', ranks: 1,
    meaning: 'Presence becomes less constrained by terrain; opens Reach.',
    effects: [{ variable: 'presence.terrainConstraint', kind: 'rule', value: 0,
      observed: 'the ground stops refusing so hard — slopes and canopy no longer shove the presence aside' }] },
  { slug: 'lift', position: 2, tier: 'inclination', name: 'Lift', ranks: 1,
    meaning: 'Raise comfortable altitude.',
    effects: [{ variable: 'presence.comfortableAltitude', kind: 'multiplier', value: 1.3,
      observed: 'the ceiling the presence drifts back down to sits visibly higher' }] },
  { slug: 'traverse', position: 3, tier: 'inclination', name: 'Traverse', ranks: 1,
    meaning: 'Increase movement speed, especially above terrain.',
    effects: [{ variable: 'presence.speed', kind: 'multiplier', value: 1.25,
      observed: 'the island crosses faster; the same flight covers more valleys' }] },
  { slug: 'far-sight', position: 4, tier: 'inclination', name: 'Far Sight', ranks: 1,
    meaning: 'Greater perceptual distance; earlier recognition of distant signals.',
    effects: [{ variable: 'perception.distance', kind: 'multiplier', value: 1.4,
      observed: 'smoke, herds and figures resolve into named things further out' }] },
  { slug: 'ascension', position: 5, tier: 'practice', name: 'Ascension', ranks: 2,
    meaning: 'I: lower Impulse cost above the comfortable altitude. II: a gentler exertion curve at extreme altitude.',
    effects: [{ variable: 'impulse.cost.altitude', kind: 'cost', value: 0.7,
      observed: 'climbing past the comfortable ceiling stops draining the god so quickly' }] },
  { slug: 'broad-horizon', position: 6, tier: 'practice', name: 'Broad Horizon', ranks: 2,
    meaning: 'I–II: height converts into a larger maximum Focus footprint.',
    effects: [{ variable: 'focus.maxRadius.fromAltitude', kind: 'multiplier', value: 1.3,
      observed: 'the same altitude now permits a wider cast than it did — the wheel runs further before it stops' }] },
  { slug: 'swift-passage', position: 7, tier: 'practice', name: 'Swift Passage', ranks: 1,
    meaning: 'Traversal speed increases strongly with altitude.',
    effects: [{ variable: 'presence.speed.fromAltitude', kind: 'multiplier', value: 1.5,
      observed: 'height buys speed: high flight crosses watersheds in the time low flight crosses a valley' }] },
  { slug: 'the-high-air', position: 8, tier: 'mastery', name: 'The High Air', ranks: 3,
    meaning: 'I: comfortable above forest and hill. II: mountains traversable in comfort. III: kilometre-scale altitude becomes ordinary.',
    effects: [{ variable: 'presence.comfortableAltitude', kind: 'cap', value: 3200,
      observed: 'the peaks stop being a ceiling; the whole island lies below without effort' }] },
  { slug: 'distant-attention', position: 9, tier: 'mastery', name: 'Distant Attention', ranks: 2,
    meaning: 'I: Objects of Care stay legible further away. II: known subjects can be focused precisely at extreme distance, where perception permits.',
    effects: [{ variable: 'perception.objectOfCare.range', kind: 'multiplier', value: 2.5,
      observed: 'a followed life stays named and findable from altitudes where everyone else is a dot' }],
    qualifies: { id: 'reach.kept-a-distant-care',
      describes: 'have kept an Object of Care legible across a real separation — followed something that travelled away from you and did not lose it' } },
  { slug: 'horizon-without-end', position: 10, tier: 'transcendence', name: 'Horizon Without End', ranks: 3,
    meaning: 'I–III: extreme horizontal travel becomes progressively cheaper and faster.',
    effects: [{ variable: 'travel.cost.horizontal', kind: 'cost', value: 0.4,
      observed: 'distance stops being a cost at all; the far coast is a decision, not a journey' }],
    qualifies: { id: 'reach.crossed-the-world',
      describes: 'have acted consequentially at both ends of the world — divine history on ground more than a region apart' } },
  { slug: 'the-world-below', position: 11, tier: 'transcendence', name: 'The World Below', ranks: 1,
    meaning: 'At sufficient altitude, broad subjects resolve as coherent systems: watersheds, migrations, settlement networks, ecological regions.',
    effects: [{ variable: 'perception.systemResolution', kind: 'rule', value: 0,
      observed: 'from high enough the island stops being terrain and reads as systems — where the water goes, where the herds go, where the people are' }],
    qualifies: { id: 'reach.saw-a-system-whole',
      describes: 'have witnessed a whole system change — a migration, a watershed or a settlement network measurably different from when you first saw it' } },
];

/** D §8.4. */
const ROOT: readonly NodeSpec[] = [
  { slug: 'sense-of-place', position: 1, tier: 'awakening', name: 'Sense of Place', ranks: 1,
    meaning: 'Resonance becomes legible.',
    effects: [{ variable: 'resonance.legible', kind: 'rule', value: 0,
      observed: 'ground you have a history with reads differently — the world says so, without a number' }] },
  { slug: 'lingering-touch', position: 2, tier: 'inclination', name: 'Lingering Touch', ranks: 1,
    meaning: 'Local influence lasts slightly longer.',
    effects: [{ variable: 'env.effect.decayRate', kind: 'multiplier', value: 0.8,
      observed: 'a blessing stays green on the ground for weeks longer than it used to' }] },
  { slug: 'familiar-earth', position: 3, tier: 'inclination', name: 'Familiar Earth', ranks: 1,
    meaning: 'Acts become more efficient in places with meaningful history.',
    effects: [{ variable: 'act.cost.familiarGround', kind: 'cost', value: 0.8,
      observed: 'the same cast costs less on ground you have worked before' }] },
  { slug: 'remembered-ground', position: 4, tier: 'inclination', name: 'Remembered Ground', ranks: 1,
    meaning: 'Historical traces become easier to perceive in familiar places.',
    effects: [{ variable: 'history.traceLegibility', kind: 'multiplier', value: 1.4,
      observed: 'old camps, worn roads and ruins declare themselves where you have spent time' }] },
  { slug: 'resonance', position: 5, tier: 'practice', name: 'Resonance', ranks: 2,
    meaning: 'I: meaningful interaction builds Resonance faster. II: deep Resonance decays more slowly.',
    effects: [
      { variable: 'resonance.gainRate', kind: 'multiplier', value: 1.4,
        observed: 'a place binds to you in years rather than decades' },
      { variable: 'resonance.decayRate', kind: 'multiplier', value: 0.6,
        observed: 'somewhere you left long ago still knows you' }] },
  { slug: 'anchor', position: 6, tier: 'practice', name: 'Anchor', ranks: 2,
    meaning: 'I: a stronger Anchor relationship. II: the Anchor becomes a weak extension of perception.',
    effects: [
      { variable: 'anchor.strength', kind: 'multiplier', value: 1.35,
        observed: 'the stone holds more of you; mortals raise its sacred landscape faster' },
      { variable: 'anchor.perception', kind: 'rule', value: 0,
        observed: 'you know when something happens at your stone, wherever you are' }] },
  { slug: 'home-ground', position: 7, tier: 'practice', name: 'Home Ground', ranks: 1,
    meaning: 'Movement, perception and intervention all improve on deeply Resonant ground.',
    effects: [{ variable: 'homeGround.efficiency', kind: 'multiplier', value: 1.3,
      observed: 'you are simply better where you belong — faster, further-seeing, cheaper to act' }] },
  { slug: 'known-earth', position: 8, tier: 'mastery', name: 'Known Earth', ranks: 3,
    meaning: 'I: sense major change. II: sense promoted and significant subjects. III: perceive broad ecological and social state through the place itself.',
    effects: [{ variable: 'place.sense.state', kind: 'rule', value: 0,
      observed: 'the ground reports: what burned, what died, who arrived, what has become important since you last stood here' }],
    qualifies: { id: 'root.a-place-with-a-century',
      describes: 'have held one place through a century of its own history — resonance sustained across generations of whoever lives there' } },
  { slug: 'extended-presence', position: 9, tier: 'mastery', name: 'Extended Presence', ranks: 2,
    meaning: 'I: a weak Aura remains around a deep Anchor. II: multiple Anchors carry fragments of local Presence.',
    effects: [{ variable: 'aura.fromAnchor', kind: 'rule', value: 0,
      observed: 'the ground around your stone keeps changing while you are elsewhere' }],
    qualifies: { id: 'root.left-something-behind',
      describes: 'have an Anchor whose place kept changing in your absence — consequence at a stone you were not standing at' } },
  { slug: 'sacred-geography', position: 10, tier: 'transcendence', name: 'Sacred Geography', ranks: 3,
    meaning: 'I: Resonant places reinforce one another. II: meaningful paths and places form a network. III: the network functions as one coherent divine landscape.',
    effects: [{ variable: 'anchor.network', kind: 'rule', value: 0,
      observed: 'your stones stop being separate — the roads between them are yours too, and the land between reads as one holding' }],
    qualifies: { id: 'root.three-bound-places',
      describes: 'have three deeply Resonant places connected by ground mortals actually walk between' } },
  { slug: 'the-place-knows-me', position: 11, tier: 'transcendence', name: 'The Place Knows Me', ranks: 1,
    meaning: 'Deep local Resonance enables broad low-intensity action without the altitude it would normally require.',
    effects: [{ variable: 'focus.maxRadius.fromResonance', kind: 'rule', value: 0,
      observed: 'standing in your own valley you can mean the whole valley, from the ground' }],
    qualifies: { id: 'root.the-deepest-ground',
      describes: 'have a place at the top of the Resonance scale — the single most bound ground of your existence' } },
];

/* ── Beasts — the first direction written to the 41-point shape ────────── */

/**
 * BEASTS asks: what does a god come to understand about non-human life that
 * chooses, learns, moves, hunts, flees, associates and remembers?
 *
 * Three Currents run through it and are tagged on the nodes as metadata —
 * **herd** (populations, ground, movement, collective decision), **blood**
 * (particular animals, kinship, lines) and **voice** (signal, learning,
 * memory, communication) — and the three Epics in `epics.ts` are the final
 * readings of each. Nothing forces a choice among them before the Epic.
 *
 * ## What is behind each node — the honest split
 *
 * The simulation keeps wildlife as **statistics on the social grid**: a head
 * count per species per 1,024 m cell, a carrying capacity read daily from
 * habitat, sere, season and range wear, predation and carrion between the
 * kinds, and a migration pass that walks herds toward better headroom
 * (`sim/wildlife/`). No animal is an entity; no herd is one either — a
 * "herd" the pointer names is one cell's population of one kind. There is
 * no individual, no kinship, no learning, no memory, no signal.
 *
 * So the inner layers read and press exactly that truth, and every one of
 * them has a consumer (`bindings.ts` marks them `built`; `test/wiring.ts` §8
 * refuses a `built` without a reader): what a herd is doing about its ground,
 * who it keeps company with, its range and its edge, its recent movement,
 * how it compares to its neighbours, what the season and the hunters and the
 * carrion say, and — the first Beast ACT — a Strengthen or Weaken that lands
 * on a herd's own inclination to move, to increase, or to hunt, and on
 * nothing where no such inclination exists.
 *
 * The outer Blood and Voice nodes are written to the design and marked
 * `absent`, in words the panel prints: *no animal is an individual to the
 * simulation*, *no animal learns*, *animals remember nothing*. They are here
 * so the direction has its shape and so the gap is visible in the tool that
 * would otherwise hide it, not so a point can be spent on nothing unawares.
 * `WIRING.md` carries the row.
 *
 * Forty-four ordinary points, so the forty an Epic asks for leave a real
 * choice of four; and each layer holds more than the next one's floor.
 */
const BEASTS: readonly NodeSpec[] = [
  /* ── awakening: READ ─────────────────────────────────────────────────── */
  { slug: 'read-the-wild', position: 1, tier: 'awakening', name: 'Read the Wild', ranks: 1,
    meaning: 'Selecting an animal or a herd tells you what is driving it right now — increase, hunger, flight, the hunt — wherever the world can truthfully say.',
    effects: [{ variable: 'beast.read.drive', kind: 'rule', value: 0,
      observed: 'the card stops at "at grass" for everyone else; for you it says what they are doing about their ground — gathering to it, thinning on it, walking off it, hunting it hungry' }] },

  /* ── inclination: READ, wider ────────────────────────────────────────── */
  { slug: 'living-company', position: 2, tier: 'inclination', name: 'Living Company', ranks: 1, current: 'herd',
    meaning: 'What an animal keeps company with — alone, paired, one of a few, one of a herd — and whether more of its kind stand on the ground around it.',
    effects: [{ variable: 'beast.read.company', kind: 'rule', value: 0,
      observed: 'a hunter reads as one, or a pair, or several; a herd reads by whether its kind holds the country around it too' }] },
  { slug: 'known-range', position: 2, tier: 'inclination', name: 'Known Range', ranks: 3, current: 'herd',
    meaning: 'I: where this kind normally stands around here, and whether this ground is the middle of it or the edge. II–III: the same reading, a ring of country further out each time.',
    effects: [
      { variable: 'beast.read.range', kind: 'rule', value: 0,
        observed: '"the middle of their country" or "the edge of it" or "further out than their kind stands anywhere near"; and ground they were on lately and are gone from' },
      { variable: 'beast.read.reach', kind: 'add', value: 1,
        observed: 'each rank widens the country your beast readings survey by one ring of ground' }] },
  { slug: 'sign-on-the-ground', position: 3, tier: 'inclination', name: 'Sign on the Ground', ranks: 2, current: 'voice',
    meaning: 'I: TRACE reads recent movement — where they came onto this ground from, where some of them left for. II: whether they fled ground that failed them or drifted after better.',
    effects: [{ variable: 'beast.trace.movement', kind: 'rule', value: 0,
      observed: '"came onto this ground from the west lately" · "some of them walked off north" — from what the world remembers of the last weeks, never longer' }] },
  { slug: 'animal-measure', position: 3, tier: 'inclination', name: 'Animal Measure', ranks: 1, current: 'blood',
    meaning: 'COMPARE sets this herd against the others of its kind around it: denser or thinner, growing while they decline, on worn ground while theirs is whole.',
    effects: [{ variable: 'beast.compare', kind: 'rule', value: 0,
      observed: '"thinner on the ground than the herds around them" · "doing better than their neighbours" — differences the simulation carries, never invented ones' }] },
  { slug: 'where-they-water', position: 3, tier: 'inclination', name: 'Where They Water', ranks: 1, current: 'herd',
    meaning: 'What the season is doing to their ground: the late-summer cure on grass with no water, and why the herds keep to the river.',
    effects: [{ variable: 'beast.read.season', kind: 'rule', value: 0,
      observed: '"the grass here is curing in the dry, and the ground by the water is not" — the seasonal range, said out loud' }] },
  { slug: 'what-the-dead-feed', position: 3, tier: 'inclination', name: 'What the Dead Feed', ranks: 1,
    meaning: 'The scavengers\' side of every death: what is lying on this ground and what it is drawing.',
    effects: [{ variable: 'beast.read.carrion', kind: 'rule', value: 0,
      observed: '"something is dying on this ground — the scavengers are gathering to it"' }] },
  { slug: 'familiar-blood', position: 3, tier: 'inclination', name: 'Familiar Blood', ranks: 1, current: 'blood',
    meaning: 'A particular animal becomes recognisable across sightings — this one, not one of them.',
    effects: [{ variable: 'beast.read.individual', kind: 'rule', value: 0,
      observed: 'the card would say "the same one you found before" — it cannot yet: no animal is an individual to the simulation' }] },

  /* ── practice: ACT ───────────────────────────────────────────────────── */
  { slug: 'predator-and-prey', position: 4, tier: 'practice', name: 'Predator and Prey', ranks: 2,
    meaning: 'I: TRACE moves from a hunter to what carries it — the prey standing on its ground, thin or plentiful. II: and from a herd to what hunts it, and how hard.',
    effects: [{ variable: 'beast.trace.prey', kind: 'rule', value: 0,
      observed: '"hungrier than this ground can feed — the prey here has thinned" on a hunter; "hunted hard on this ground" on the herd' }] },
  { slug: 'human-edge', position: 4, tier: 'practice', name: 'Human Edge', ranks: 2,
    meaning: 'I: whether people are out on this ground and hunting it. II: whether people have met these hunters here lately.',
    effects: [{ variable: 'beast.read.mortal', kind: 'rule', value: 0,
      observed: '"a hunting party is on this ground" · "people met these hunters here within the month"' }] },
  { slug: 'migration-thread', position: 5, tier: 'practice', name: 'Migration Thread', ranks: 1, current: 'herd',
    meaning: 'Strengthen, aimed at a herd already inclined to leave its ground, reinforces the leaving; Weaken holds them. Only when the inclination is real — a settled herd refuses it.',
    effects: [{ variable: 'beast.press.movement', kind: 'rule', value: 0,
      observed: 'the card offers "their drift off this ground" under Strengthen and the herd walks out over the following days — or refuses you: "nothing in them is inclined to move"' }] },
  { slug: 'green-years', position: 5, tier: 'practice', name: 'Green Years', ranks: 1, current: 'herd',
    meaning: 'Strengthen, aimed at a herd gathering onto ground with room for it, reinforces the increase.',
    effects: [{ variable: 'beast.press.increase', kind: 'rule', value: 0,
      observed: 'the card offers "their increase" while the ground can carry more of them, and the herd fills its ground faster than it would have' }] },
  { slug: 'hunt-as-one', position: 5, tier: 'practice', name: 'Hunt as One', ranks: 1, current: 'voice',
    meaning: 'Strengthen, aimed at hunters with prey on their ground, reinforces the hunt itself.',
    effects: [{ variable: 'beast.press.hunt', kind: 'rule', value: 0,
      observed: 'the card offers "their hunting" while there is prey to hunt, and the herds on that ground thin faster' }] },
  { slug: 'the-herd-answers', position: 6, tier: 'practice', name: 'The Herd Answers', ranks: 3, current: 'herd',
    meaning: 'I–III: your hand on what a herd is doing lands harder each time.',
    effects: [{ variable: 'beast.work.movement', kind: 'multiplier', value: 1.3,
      observed: 'the same cast moves more of them, sooner' }] },
  { slug: 'light-hand', position: 6, tier: 'practice', name: 'Light Hand', ranks: 2,
    meaning: 'I–II: acting on animals costs you less.',
    effects: [{ variable: 'impulse.cost.beastTarget', kind: 'cost', value: 0.85,
      observed: 'the bar quotes a lower price the moment the pointer is on a herd' }] },
  { slug: 'omen-to-the-wild', position: 6, tier: 'practice', name: 'Omen to the Wild', ranks: 1, current: 'voice',
    meaning: 'A sign given to animals — this one, the group near it, whatever uses this ground — read by their own drives, never obeyed.',
    effects: [{ variable: 'beast.omen.audience', kind: 'rule', value: 0,
      observed: 'the wild would notice a sign — it cannot yet: wildlife never hears an omen' }] },
  { slug: 'follow-the-line', position: 6, tier: 'practice', name: 'Follow the Line', ranks: 1, current: 'blood',
    meaning: 'REMEMBER outlives one animal: not this one, but this one and what comes after it.',
    effects: [{ variable: 'beast.remember.lineage', kind: 'rule', value: 0,
      observed: 'the thread would carry to descendants — it cannot yet: no animal has parents or young' }] },

  /* ── mastery: CONNECT ────────────────────────────────────────────────── */
  { slug: 'population-as-subject', position: 7, tier: 'mastery', name: 'Population as Subject', ranks: 1, current: 'herd',
    meaning: 'Scrolling out from a herd resolves the local population — every ground around it their kind stands on — as the subject: how they are doing as a whole.',
    effects: [{ variable: 'beast.subject.population', kind: 'rule', value: 0,
      observed: 'past the herd itself the card reads "the grazers of this country" — how much ground they hold, whether it is filling or emptying, where it is worn and where it is hunted' }] },
  { slug: 'territorial-world', position: 7, tier: 'mastery', name: 'Territorial World', ranks: 1, current: 'herd',
    meaning: 'A population\'s ground as a thing with edges: shrinking, spreading, and shared with what hunts it.',
    effects: [{ variable: 'beast.read.territory', kind: 'rule', value: 0,
      observed: '"they have lost ground this year" · "they have come back onto ground they were gone from" · "hunters stand on most of the ground they hold"' }] },
  { slug: 'collective-decision', position: 8, tier: 'mastery', name: 'Collective Decision', ranks: 1, current: 'voice',
    meaning: 'You recognise a herd close to a real branch — stay or leave, thrive or fail — without being told which way it will go.',
    effects: [{ variable: 'beast.read.branch', kind: 'rule', value: 0,
      observed: '"close to leaving this ground" · "the ground is failing under them" — something is undecided here, and the card says so before it resolves' }] },
  { slug: 'long-watch', position: 8, tier: 'mastery', name: 'Long Watch', ranks: 3,
    meaning: 'I–III: your hand on a herd stays on it longer before the wild forgets it.',
    effects: [{ variable: 'beast.press.persistence', kind: 'multiplier', value: 1.5,
      observed: 'a pressed herd keeps answering for days longer' }] },
  { slug: 'lineage-as-subject', position: 9, tier: 'mastery', name: 'Lineage as Subject', ranks: 1, current: 'blood',
    meaning: 'From one animal to its parents, its siblings, its descendants; the line itself becomes something to care about.',
    effects: [{ variable: 'beast.subject.lineage', kind: 'rule', value: 0,
      observed: 'it cannot yet: no animal has kin' }] },
  { slug: 'animal-culture', position: 9, tier: 'mastery', name: 'Animal Culture', ranks: 1, current: 'voice',
    meaning: 'Repeated learned behaviour in a group becomes a subject: raiding, avoidance, a way of hunting.',
    effects: [{ variable: 'beast.subject.culture', kind: 'rule', value: 0,
      observed: 'it cannot yet: no animal learns' }] },
  { slug: 'shared-memory', position: 9, tier: 'mastery', name: 'Shared Memory', ranks: 1, current: 'voice',
    meaning: 'Places and events that changed how a group behaves become legible as its memory.',
    effects: [{ variable: 'beast.read.memory', kind: 'rule', value: 0,
      observed: 'it cannot yet: animals remember nothing' }] },

  /* ── transcendence: SHAPE · PERSIST · BECOME ─────────────────────────── */
  { slug: 'wild-awareness', position: 10, tier: 'transcendence', name: 'Wild Awareness', ranks: 1,
    meaning: 'What matters to the wild reaches you wherever you are: a great migration, a kind gone from a valley, a kind come back to one.',
    effects: [{ variable: 'beast.aware.wild', kind: 'rule', value: 0,
      observed: 'the Chronicle carries the herds\' great changes to you from across the island, while every other god hears only what happens near them' }] },
  { slug: 'ecological-role', position: 10, tier: 'transcendence', name: 'Ecological Role', ranks: 2,
    meaning: 'I: TRACE runs from a herd to the ground under it — burn, season, wear, water — and says why the ground carries what it does. II: and on across the chain, hunter to prey to grass.',
    effects: [{ variable: 'beast.trace.role', kind: 'rule', value: 0,
      observed: '"the burn\'s grass years are carrying them" · "the range is worn under them" · "the hunters here live on the browsers"' }] },
  { slug: 'great-movement', position: 11, tier: 'transcendence', name: 'Great Movement', ranks: 2, current: 'herd', floor: 24,
    meaning: 'I: a wide cast reaches every inclined herd in its footprint, diffusely — more ground, less authorship. II: it lands harder.',
    effects: [
      { variable: 'beast.press.movement.wide', kind: 'rule', value: 0,
        observed: 'a valley-wide Strengthen leans on every herd in it that was already inclined to move, and on none that were not' },
      { variable: 'beast.work.movement.wide', kind: 'multiplier', value: 1.4,
        observed: 'the wide hand lands harder' }] },
  { slug: 'ancestral-thread', position: 11, tier: 'transcendence', name: 'Ancestral Thread', ranks: 1, current: 'blood', floor: 30,
    meaning: 'A line of animals persists as an Object of Care past any one lifespan.',
    effects: [{ variable: 'beast.care.lineage', kind: 'rule', value: 0,
      observed: 'it cannot yet: no animal has kin, so there is no line to keep' }] },
  { slug: 'the-wild-remembers', position: 11, tier: 'transcendence', name: 'The Wild Remembers', ranks: 1, current: 'voice', floor: 30,
    meaning: 'A learned group behaviour outlives the animals that began it.',
    effects: [{ variable: 'beast.culture.persistent', kind: 'rule', value: 0,
      observed: 'it cannot yet: no animal learns' }] },
  { slug: 'a-sign-carried', position: 12, tier: 'transcendence', name: 'A Sign Carried', ranks: 1, current: 'voice', floor: 30,
    meaning: 'A sign one animal read travels through real animal learning to others — never a broadcast.',
    effects: [{ variable: 'beast.omen.carried', kind: 'rule', value: 0,
      observed: 'it cannot yet: wildlife hears no sign and carries nothing' }] },
  { slug: 'pressure-through-generations', position: 12, tier: 'transcendence', name: 'Pressure Through Generations', ranks: 1, current: 'blood', floor: 36,
    meaning: 'The slowest hand there is: diffuse, environment-selected, never guaranteed pressure on what a kind inherits.',
    effects: [{ variable: 'beast.press.heritable', kind: 'rule', value: 0,
      observed: 'it cannot yet — the trait substrate that would carry it (world/strain.ts) is complete and deliberately inert; waking it is parked' }] },
  { slug: 'speak-without-command', position: 12, tier: 'transcendence', name: 'Speak Without Command', ranks: 1, current: 'voice', floor: 36,
    meaning: 'Your signs are recognised as significant by animals far more reliably. Recognition is not obedience: a hungry hunter can understand a warning and still choose hunger.',
    effects: [{ variable: 'beast.omen.recognised', kind: 'rule', value: 0,
      observed: 'it cannot yet: wildlife never hears an omen' }] },
];

/**
 * The eleven directions `DESIGN.md` has not yet worked (Appendix B.2). Each
 * carries its Awakening rung and nothing else, so the spoke is real, its
 * Epics are reachable in principle, and the eleven gaps are visible.
 *
 * Their Awakening deliberately has **no effects**: naming a simulation
 * variable here would be inventing design rather than transcribing it, and
 * `test/godhood.ts` asserts that only unworked directions are allowed to do
 * this.
 */
const awakening = (d: Direction): WebNode => ({
  id: `${d.id}.awakening`,
  direction: d.id,
  position: 1,
  tier: 'awakening',
  name: d.name,
  ranks: 1,
  meaning: `Enter the philosophy. ${d.identity}`,
  effects: [],
});

/**
 * THE ONE BECOME MOMENT — `PLAYABLE.md` P5, 2026-08-22.
 *
 * Mortals is not worked; D Appendix B.2 still owes it. This is one node and
 * not the direction: the smallest honest route from a consequence that
 * persisted to `Tab` recognising that something has become possible because
 * of how the god acted. It asks for the becoming `test/horizon-c.ts` built —
 * a press that carried a life over a line its neighbours speak at, which the
 * world confirmed (`Endurance` `becoming`) — and what it opens is a change
 * to the RULE of a verb, never a percentage: a Strengthen on somebody reaches
 * what they have lately become, and not only what today's work asks of them.
 * `DECISIONS.md`: Strengthen means *reinforce something already present,
 * expressed, or occurring*; a becoming is expressed in a life's own history.
 *
 * Law 25 is structural here: the qualification is an endurance, endurances
 * come only from the world confirming a change, and the same press on the
 * same life folds into one strand — so no amount of repetition reaches it.
 * `test/become.ts` holds that a fresh god cannot.
 *
 * The direction stays unworked, the panel still draws the rest of its spoke
 * empty, and nothing else is invented for it.
 */
const MORTALS_FIRST: readonly NodeSpec[] = [
  { slug: 'took-to-it', position: 2, tier: 'inclination', name: 'Took to It', ranks: 1,
    meaning: 'A Strengthen on a person also deepens what they have lately become — in the direction they became it — not only what the day\'s work asks of them.',
    effects: [{ variable: 'press.reach.becoming', kind: 'rule', value: 0,
      observed: 'somebody who lately grew bold can be leaned on for their nerve even by the fire, where the day asks nothing of it' }],
    qualifies: { id: 'mortals.a-life-became',
      describes: 'have laid a hand on a life and had the world say they became something — a press that carried somebody over a line their neighbours speak at' } },
];

export const NODES: readonly WebNode[] = [
  ...spoke('reach', REACH),
  ...spoke('root', ROOT),
  ...spoke('beasts', BEASTS),
  ...DIRECTIONS.filter((d) => !d.worked).map(awakening),
  ...spoke('mortals', MORTALS_FIRST),
];

/* ── crossroads (D §10) ────────────────────────────────────────────────── */

/**
 * D §10 gives names, adjacencies and what each *could represent* — no
 * statistics — so these carry no effects yet, and the test permits that for
 * crossroads specifically. D §10's own worked example sets the shape: 2–4
 * points, five points in each adjacent direction, plus historical
 * qualification.
 *
 * Note that "Known Earth" is both a Root node (D §8.4 position 8) and a
 * crossroad here (Root + Insight). That collision is the document's, not a
 * transcription error; the ids differ, so nothing breaks, but one of the two
 * wants renaming in the next design pass.
 */
export const CROSSROADS: readonly Crossroad[] = [
  { id: 'crossroad.ancient-grove', name: 'Ancient Grove', between: ['life', 'root'], cost: 3, requiresEach: 5,
    represents: 'A living place whose organisms and Resonance reinforce one another over centuries.', effects: [] },
  { id: 'crossroad.old-migration', name: 'The Old Migration', between: ['beasts', 'order'], cost: 3, requiresEach: 5,
    represents: 'A persistent, inherited migration system with strong route memory.', effects: [] },
  { id: 'crossroad.passing-presence', name: 'Passing Presence', between: ['reach', 'aura'], cost: 2, requiresEach: 5,
    represents: 'A roaming deity leaves broad temporary influence rather than deep Root.', effects: [] },
  { id: 'crossroad.shared-hearth', name: 'Shared Hearth', between: ['mortals', 'beasts'], cost: 3, requiresEach: 5,
    represents: 'Domestication, mutualism, stewardship, shared landscapes.', effects: [] },
  { id: 'crossroad.succession', name: 'Succession', between: ['life', 'death'], cost: 4, requiresEach: 5,
    represents: 'Disturbance, decomposition, regrowth and turnover as one comprehensible process.', effects: [] },
  { id: 'crossroad.adaptive-tradition', name: 'Adaptive Tradition', between: ['order', 'chaos'], cost: 4, requiresEach: 5,
    represents: 'Stable institutions and practices that deliberately absorb controlled novelty.', effects: [] },
  { id: 'crossroad.perfect-leverage', name: 'Perfect Leverage', between: ['might', 'insight'], cost: 4, requiresEach: 5,
    represents: 'Raw work applied at the highest-leverage causal point.', effects: [] },
  { id: 'crossroad.known-earth', name: 'Known Earth', between: ['root', 'insight'], cost: 3, requiresEach: 5,
    represents: 'Deep sensing of local pressure and downstream consequence.', effects: [] },
  { id: 'crossroad.divine-hand', name: 'Divine Hand', between: ['touch', 'might'], cost: 3, requiresEach: 5,
    represents: 'Extreme material and tactile intervention.', effects: [] },
  { id: 'crossroad.cultural-stewardship', name: 'Cultural Stewardship', between: ['mortals', 'communion'], cost: 3, requiresEach: 5,
    represents: 'Belief carried through voluntary practice and institutions.', effects: [] },
];

/* ── lookups ───────────────────────────────────────────────────────────── */

/**
 * The static content a `GodhoodWeb` walks. The shipped Web is `WEB_DATA`
 * below; `test/godhood.ts` hands in a synthetic one to prove the engine's
 * arithmetic — two Epics, never three — against directions written to the
 * full 41-point shape, which only Beasts is today.
 */
export interface WebData {
  nodes: readonly WebNode[];
  epics: readonly Epic[];
  crossroads: readonly Crossroad[];
}

/**
 * The indexes the engine reads through: node by id, nodes by direction in
 * spoke order, capacity and depth floors. One instance per `WebData`, and the
 * module-level lookups below are the shipped instance's.
 */
export class WebIndex {
  private readonly byId: Map<string, WebNode>;
  private readonly byDirection: Map<DirectionId, WebNode[]>;

  constructor(readonly data: WebData) {
    this.byId = new Map(data.nodes.map((n) => [n.id, n]));
    this.byDirection = new Map<DirectionId, WebNode[]>(
      DIRECTIONS.map((d) => [d.id, data.nodes.filter((n) => n.direction === d.id)
        .sort((a, b) => a.position - b.position || (a.id < b.id ? -1 : 1))]));
  }

  nodeById(id: string): WebNode | undefined { return this.byId.get(id); }
  nodesOf(d: DirectionId): readonly WebNode[] { return this.byDirection.get(d) ?? []; }
  epicById(id: string): Epic | undefined { return this.data.epics.find((e) => e.id === id); }
  crossroadById(id: string): Crossroad | undefined {
    return this.data.crossroads.find((c) => c.id === id);
  }

  /** Ordinary points a direction can absorb before its Epic — its written capacity. */
  ordinaryPointsIn(d: DirectionId): number {
    return this.nodesOf(d).reduce((n, node) => n + node.ranks, 0);
  }

  /** Ordinary points in every tier strictly beneath this one. */
  pointsBelowTier(d: DirectionId, tier: NodeTier): number {
    return this.nodesOf(d)
      .filter((n) => TIER_ORDER.indexOf(n.tier) < TIER_ORDER.indexOf(tier))
      .reduce((sum, n) => sum + n.ranks, 0);
  }

  /**
   * How deep a god must already be in the direction — ordinary points held
   * OUTSIDE this node — before it may buy this node.
   *
   * Two terms, and the max of them:
   *  · the tier's floor (`TIER_FLOOR`), capped at what the direction actually
   *    holds beneath the tier — so a direction written to the old thin shape
   *    (Reach, Root: 14 points below transcendence) degrades to exactly the
   *    old every-node rule rather than sealing its outer nodes for ever;
   *  · the node's own explicit floor, capped at every other point in the
   *    direction — the deep BECOME nodes at 30 and 36.
   */
  floorOf(node: WebNode): number {
    const tier = Math.min(TIER_FLOOR[node.tier], this.pointsBelowTier(node.direction, node.tier));
    const own = node.floor === undefined ? 0
      : Math.min(node.floor, this.ordinaryPointsIn(node.direction) - node.ranks);
    return Math.max(tier, own);
  }
}

/** D §8's layers, outward. */
export const TIER_ORDER: readonly NodeTier[] =
  ['awakening', 'inclination', 'practice', 'mastery', 'transcendence', 'epic'];

/** The shipped Web: every node, Epic and crossroad above and in `epics.ts`. */
export const WEB_DATA: WebData = { nodes: NODES, epics: EPICS, crossroads: CROSSROADS };
const SHIPPED = new WebIndex(WEB_DATA);
export const shippedIndex = (): WebIndex => SHIPPED;

export const nodeById = (id: string): WebNode | undefined => SHIPPED.nodeById(id);
export const nodesOf = (d: DirectionId): readonly WebNode[] => SHIPPED.nodesOf(d);
export const directionById = (d: DirectionId): Direction =>
  DIRECTIONS.find((x) => x.id === d)!;
export const axisOf = (d: DirectionId): Axis =>
  AXES.find((a) => a.poles.includes(d))!;
/** The other end of this direction's tension. D §7's opposites. */
export const oppositeOf = (d: DirectionId): DirectionId => {
  const [a, b] = axisOf(d).poles;
  return a === d ? b : a;
};

/** Ordinary points a direction can absorb before its Epic. */
export const ordinaryPointsIn = (d: DirectionId): number => SHIPPED.ordinaryPointsIn(d);
/** The depth a node asks for, in the shipped Web. */
export const floorOf = (node: WebNode): number => SHIPPED.floorOf(node);
/** Whether a direction has been written deep enough for its Epic to be reachable at all. */
export const writtenToEpic = (d: DirectionId): boolean => ordinaryPointsIn(d) >= EPIC_REQUIRES;
